// src/components/fx/FireworksCanvas.jsx
//
// Animated fireworks on a transparent <canvas>. Fills its
// (position:relative) parent. Pauses when off-screen / tab hidden,
// caps particles, and shows nothing for reduced-motion users.
//
// Props
//   density — how often rockets launch (1 = normal)
//   power   — burst size multiplier (1 = normal)
//   rays    — draw bursts as glowing ray streaks with a flash
//             (the classic "Diwali poster" look)
//   speed   — animation speed (1 = normal, 0.5 = half speed / slow motion)

import React, { useEffect, useRef } from 'react';

const COLORS = ['#ffd60a', '#ff006e', '#fb5607', '#3a86ff', '#8338ec', '#06d6a0', '#ffffff', '#ff4d6d', '#2ec4b6', '#ffb703'];

export default function FireworksCanvas({ density = 1, power = 1, rays = false, speed = 1, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d');
    let w = 0, h = 0, raf = 0, visible = true, last = 0, nextLaunch = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rockets = [], sparks = [], flashes = [];
    const MAX = rays ? 1400 : 700;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      w = r.width; h = r.height;
      canvas.width = Math.max(1, w * dpr); canvas.height = Math.max(1, h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const launch = () => {
      rockets.push({
        x: w * (0.05 + Math.random() * 0.9), y: h + 4,
        vy: -(h * 0.011 + Math.random() * h * 0.006 + 3.2),
        vx: (Math.random() - 0.5) * 0.8,
        targetY: h * (0.1 + Math.random() * 0.45),
        color: COLORS[(Math.random() * COLORS.length) | 0],
      });
    };

    const explode = (x, y, color) => {
      const n = Math.round((34 + Math.random() * 26) * (rays ? 1.6 : 1));
      const speed = (1.6 + Math.random() * 2.2 + Math.min(w, h) * 0.004) * power;
      const two = Math.random() < 0.35 ? COLORS[(Math.random() * COLORS.length) | 0] : color;
      for (let i = 0; i < n && sparks.length < MAX; i++) {
        const a = (Math.PI * 2 * i) / n + Math.random() * 0.15;
        const s = speed * (rays ? 0.75 + Math.random() * 0.25 : 0.55 + Math.random() * 0.45);
        sparks.push({ x, y, px: x, py: y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
          life: 1, decay: 0.010 + Math.random() * 0.010, color: i % 2 ? color : two,
          size: (1.8 + Math.random() * 1.6) * (rays ? 0.8 : 1) });
      }
      if (rays) flashes.push({ x, y, r: Math.min(w, h) * 0.22 * power, life: 1, color });
    };

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (!visible) return;
      const dt = Math.min(2, (t - last) / 16.7 || 1) * speed; last = t;

      ctx.globalCompositeOperation = 'destination-out';
      ctx.fillStyle = `rgba(0,0,0,${(rays ? 0.28 : 0.22) * (0.5 + 0.5 * speed)})`;   // slower → longer, softer trails
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      if (t > nextLaunch) {
        launch();
        if (rays && Math.random() < 0.35) launch();       // occasional double burst
        nextLaunch = t + (700 + Math.random() * 900) / (density * Math.sqrt(speed));
      }

      // flashes (soft glow at the burst centre)
      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        f.life -= 0.035 * dt;
        if (f.life <= 0) { flashes.splice(i, 1); continue; }
        const g = ctx.createRadialGradient(f.x, f.y, 0, f.x, f.y, f.r);
        g.addColorStop(0, `rgba(255,240,200,${0.35 * f.life})`);
        g.addColorStop(0.4, f.color + Math.round(40 * f.life).toString(16).padStart(2, '0'));
        g.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2); ctx.fill();
      }

      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.x += r.vx * dt; r.y += r.vy * dt; r.vy += 0.035 * dt;
        ctx.fillStyle = r.color;
        ctx.beginPath(); ctx.arc(r.x, r.y, 2, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,220,150,.5)';
        ctx.fillRect(r.x - 0.6, r.y + 2, 1.2, 7);
        if (r.y <= r.targetY || r.vy >= 0) { explode(r.x, r.y, r.color); rockets.splice(i, 1); }
      }

      ctx.lineCap = 'round';
      for (let i = sparks.length - 1; i >= 0; i--) {
        const p = sparks[i];
        p.px = p.x; p.py = p.y;
        p.vx *= 0.985; p.vy = p.vy * 0.985 + (rays ? 0.025 : 0.045) * dt;
        p.x += p.vx * dt; p.y += p.vy * dt; p.life -= p.decay * dt;
        if (p.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.globalAlpha = p.life;
        if (rays) {
          // ray streak + bright tip
          ctx.strokeStyle = p.color; ctx.lineWidth = p.size;
          ctx.beginPath(); ctx.moveTo(p.x - p.vx * 4, p.y - p.vy * 4); ctx.lineTo(p.x, p.y); ctx.stroke();
          ctx.fillStyle = '#fff8e0';
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size * 0.9, 0, Math.PI * 2); ctx.fill();
        } else {
          ctx.fillStyle = p.color;
          ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
    };

    resize();
    const ro = new ResizeObserver(resize); ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting && !document.hidden; }, { threshold: 0 });
    io.observe(canvas);
    const onVis = () => { visible = !document.hidden; };
    document.addEventListener('visibilitychange', onVis);
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf); ro.disconnect(); io.disconnect();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [density, power, rays, speed]);

  return <canvas ref={ref} className={`fx-canvas ${className}`} aria-hidden="true" />;
}
